import { AudioIcon, IconMic, IconVideo, ImgIcon } from '@/assets/images';
import { AppIcon, CustomButton } from '@/components';
import { getFileIcon } from '@/features/chats/utils';
import { useMessageFormatter, useReplyMessage } from '@/features/chats/viewmodels';
import { Username } from '@/features/users';
import { useLanguage } from '@/localization/useLanguage';
import { getLocalUserId, getMessageSenderId, millisToMinutesAndSeconds } from '@/shared/utils';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * Renders the quoted/reply preview inside a chat bubble's chat-box.
 * Matches the div structures from the MessageBubble reference HTML:
 *  - text / link  → <div className="reply-chat">
 *  - image/video  → <div className="reply-img ct-flex ct-fcol ct-aiStart ct-gap8 ct-relative">
 *  - audio        → <div className="reply-audio ct-flex ct-aiCenter ct-gap8">
 *  - file         → <div className="document-chat">
 */
const ReplyMessageCard = ({ replyTo, activeChatJid, className = '' }) => {
   const { t } = useLanguage();
   const { replyMessage, handleScrollToMessage } = useReplyMessage({ replyTo, activeChatJid });

   const textContent = useMessageFormatter(replyMessage?.msgBody?.message, {
      mentionedUsersIds: replyMessage?.msgBody?.mentionedUsersIds || [],
      plainText: true,
   });

   if (!replyMessage) return null;

   const hasReply = replyMessage.msgBody?.replyTo;

   const { msgType, msgBody = {} } = replyMessage;
   const { media = {} } = msgBody;
   const senderId = getMessageSenderId({ type: 'chats', message: replyMessage });
   const isSender = senderId === getLocalUserId();
   let cardTypeClass = 'reply-chat';
   let gapClass = 'ct-gap4';
   let relativeClass = '';
   let content = null;

   switch (msgType) {
      case 'image':
      case 'video': {
         cardTypeClass = 'reply-img';
         gapClass = 'ct-gap8';
         relativeClass = 'ct-relative';
         const thumbUrl = media.thumb_image ? `data:image/jpeg;base64,${media.thumb_image}` : '';
         const label = msgType === 'image' ? t('chat.PHOTO') : t('chat.VIDEO');
         const hasCaption = !!media?.caption;
         const displayValue = (media?.caption || label).replace(/\+/g, ' ');
         content = (
            <>
               <div className="ct-flex ct-aiCenter ct-gap8">
                  <AppIcon icon={msgType === 'image' ? ImgIcon : IconVideo} />
                  <p className={hasCaption ? 'reply-text' : 'reply-caption'}>{displayValue}</p>
               </div>
               {thumbUrl && <img className="imageFile" src={thumbUrl} alt={label} />}
            </>
         );
         break;
      }
      case 'audio':
      case 'audio_recorded': {
         cardTypeClass = 'reply-audio';
         const isRecording = media?.audioType === 'recording';
         const duration = media.duration ? `(${millisToMinutesAndSeconds(media.duration)})` : '';
         content = (
            <div className="ct-flex ct-aiCenter ct-gap12">
               <AppIcon icon={isRecording ? IconMic : AudioIcon} />
               <p>
                  {isRecording ? t('chat.VOICE_MESSAGE') : t('common.AUDIO')} {duration && <span>{duration}</span>}
               </p>
            </div>
         );
         break;
      }
      case 'file': {
         cardTypeClass = 'document-chat';
         const fileName = media.fileName || media.file_name || 'Document';
         content = (
            <div className={`document-info ct-flex ct-aiCenter ct-gap10 ${hasReply ? 'reply-bubble' : ''}`}>
               <AppIcon icon={getFileIcon(fileName.split('.').pop())} className="ct-icon-doc" />
               <span className="ct-ellipsis">{fileName}</span>
            </div>
         );
         break;
      }
      case 'meet': {
         const { meet = {} } = msgBody;
         content = (
            <>
               <div className="ct-flex ct-aiCenter ct-gap8">
                  <p>{t('calendar.MEET_BRAND')}</p>
               </div>
               <p className="meet-title ct-ellipsis">{meet.title}</p>
            </>
         );
         break;
      }
      default:
         content = <p className="reply-text ct-ellipsis">{textContent}</p>;
         break;
   }

   if (replyMessage?.deleteByStatus === 1 || replyMessage?.deleteStatus === 1) {
      return (
         <div className="reply-chat">
            <p>{t('chat.ORIGINAL_MESSAGE_UNAVAILABLE')}</p>
         </div>
      );
   }

   return (
      <CustomButton
         as="div"
         className={`${cardTypeClass} ct-pointer ct-flex ct-fcol ct-aiStart ${gapClass} ${relativeClass} ${hasReply ? 'reply-bubble' : ''} ${className}`}
         onClick={handleScrollToMessage}
         style={{ cursor: 'pointer' }}
      >
         <Username userId={senderId} className="username ct-ellipsis" isLocal={isSender} />
         {content}
      </CustomButton>
   );
};

ReplyMessageCard.propTypes = {
   replyTo: PropTypes.string,
   activeChatJid: PropTypes.string,
   className: PropTypes.string,
};

export default React.memo(ReplyMessageCard);
